import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { SlideOutContext } from '../../../common/slide-out/SlideOutWrapper';
import SlideOutForm from '../../../common/slide-out/SlideOutForm';
import {
  Dropdown,
  Icon,
  InlineExternalLink,
  TextField,
} from '@monash/portal-react';
import { INLINE_ERROR_MSGS } from '../../../../constants/inline-error-messages';
import { nanoid } from 'nanoid';
import c from './category-form.module.scss';
import {
  APIContext,
  deepClone,
  categoryDropdownOptions,
} from '@monash/portal-frontend-common';
import { useSnackbar } from '../../../providers/SnackbarProvider';
import Subcategories from './Subcategories';
import HistoryTimeline from '../../../common/history-timeline/HistoryTimeline';
import { CATEGORY_TYPES } from '../constants';

const CategoryForm = ({ data, links, categories, refreshData }) => {
  const { halfWidth, setOpen, open } = useContext(SlideOutContext);
  const { setCategory, deleteCategory } = useContext(APIContext);
  const { addSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [trayError, setTrayError] = useState(null);
  const [history, setHistory] = useState(data?.history);

  // form setup
  const defaultValues = {
    title: '',
    description: '',
    icon: '',
    type: '',
    illustration: '',
    subcategories: [],
  };

  const initialValues = data ? deepClone(data) : defaultValues;

  const {
    reset,
    control,
    watch,
    trigger,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: initialValues,
    values: initialValues,
  });

  const title = watch('title');

  const pathify = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };
  const path = useMemo(() => {
    return pathify(title);
  }, [title]);

  const closeSlidey = async () => {
    await refreshData();
    setOpen(false);
    setLoading(false);
  };

  // handlers
  const saveCategory = async (values) => {
    const currentCat = {
      ...values,
      path,
    };
    if (!currentCat.id) {
      // New category
      currentCat.id = nanoid();
    }

    setLoading(true);
    try {
      await setCategory(currentCat);
      addSnackbar({
        message: `Category "${currentCat.title}" saved successfully.`,
        type: 'success',
      });
      await closeSlidey();
    } catch (e) {
      const statusFamily = Math.floor(e.status / 100);
      if (statusFamily === 5) {
        setTrayError(
          `We’re having trouble saving the ${
            values.id ? 'changes' : 'category'
          } right now - please try again.`
        );
      } else if (e.status === 400 && !e.body?.error?.startsWith('zod: ')) {
        setTrayError(e.body.error);
      } else {
        setTrayError(
          <>
            We're having trouble saving the {values.id ? 'changes' : 'category'}{' '}
            - please{' '}
            <InlineExternalLink
              text="log a bug"
              link="https://admin.staff.monash/feedback"
            />
          </>
        );
      }
    }
    setLoading(false);
  };

  const removeCategory = async () => {
    const currentCat = getValues();

    if (currentCat.id) {
      setLoading(true);
      try {
        await deleteCategory(currentCat.id);
        addSnackbar({
          message: `Category "${currentCat.title}" deleted successfully.`,
          type: 'success',
        });
        await closeSlidey();
      } catch (e) {
        const statusFamily = Math.floor(e.status / 100);
        if (statusFamily === 5) {
          setTrayError(
            'We’re having trouble deleting the category right now - please try again.'
          );
        } else {
          setTrayError(
            <>
              We're having trouble deleting the category - please{' '}
              <InlineExternalLink
                text="log a bug"
                link="https://admin.staff.monash/feedback"
              />
            </>
          );
        }
      }
      setLoading(false);
    } else {
      closeSlidey();
    }
  };

  // form actions
  const formHeaderActions = [
    {
      icon: Icon.ClockReverse,
      label: 'View history',
      disabled: !history,
      modalProps: {
        title: `History`,
        content: <HistoryTimeline history={history} />,
      },
    },
    {
      icon: Icon.Trash,
      onClick: removeCategory,
      disabled: !data || links.length,
      label: 'Delete category',
    },
  ];

  const formSubmitActions = [
    {
      label: 'Save',
      onClick: handleSubmit(async (data) => saveCategory(data)),
      disabled: !isDirty,
      loading,
    },
  ];

  const icons = Object.keys(Icon).map((key) => {
    const ItemIcon = Icon[key];
    return {
      text: key,
      value: key,
      label: <ItemIcon />,
    };
  });

  useEffect(() => {
    if (open) {
      reset(initialValues);
      setTrayError(null);
    }
  }, [open, data]);

  const isTitleUnique = (title) => {
    const pathed = pathify(title);
    const existing = categories?.find((cat) => cat.path === pathed);
    return !existing || existing.id === data?.id;
  };

  const errorRow = (fields) => {
    const errored = fields.some((error) => error?.message);
    return errored ? (
      <div className={c.fieldRow}>
        {fields.map((error) => {
          return (
            <div key={error?.ref.name} className={c.error}>
              {error ? (
                <>
                  <Icon.Alert size={16} />
                  <div role="status" aria-live="polite" className={c.msg}>
                    {error.message}
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    ) : null;
  };

  return (
    <SlideOutForm
      width={halfWidth}
      name={title}
      heading={title}
      headerActions={formHeaderActions}
      submitActions={formSubmitActions}
      submitError={trayError}
      {...{ isDirty }}
    >
      <div className={c.form}>
        <div>
          <div className={c.fieldRow}>
            <Controller
              control={control}
              name="title"
              rules={{
                required: INLINE_ERROR_MSGS.TITLE,
                validate: (val) => {
                  if (!isTitleUnique(val)) {
                    return INLINE_ERROR_MSGS.TITLE_UNIQUE;
                  }
                },
              }}
              render={({ field: { onBlur, onChange, value } }) => (
                <div className={c.field}>
                  <label>Category title</label>
                  <TextField
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    placeholder="New Category"
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="icon"
              rules={{
                required: INLINE_ERROR_MSGS.ICON,
              }}
              render={({ field: { onChange, value } }) => (
                <div className={c.field}>
                  <label>Icon</label>
                  <Dropdown
                    value={value}
                    data={icons}
                    searchable
                    onChange={onChange}
                    onClose={() => trigger('icon')}
                  />
                </div>
              )}
            />
          </div>
          {errorRow([errors.title, errors.icon])}
        </div>
        <Controller
          control={control}
          name="description"
          rules={{
            required: INLINE_ERROR_MSGS.DESCRIPTION,
          }}
          render={({ field: { onBlur, onChange, value } }) => (
            <div className={c.field}>
              <label>Category description</label>
              <TextField
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                error={() => errors.description}
                errorMsg={errors.description?.message}
              />
            </div>
          )}
        />

        <div className={c.field}>
          <label>Path</label>
          <div className={c.withDecoration}>
            <span className={c.decorationText}>links.student.monash/</span>
            <TextField disabled value={path} />
          </div>
        </div>

        <Controller
          control={control}
          name="illustration"
          rules={{
            required: INLINE_ERROR_MSGS.ILLUSTRATION,
          }}
          render={({ field: { onChange, value } }) => (
            <div className={c.field}>
              <label>Illustration</label>
              <span>
                <Dropdown
                  value={value}
                  data={categoryDropdownOptions}
                  onChange={onChange}
                  onClose={() => trigger('illustration')}
                />
                {errorRow([errors.illustration])}
              </span>
            </div>
          )}
        />

        <Controller
          control={control}
          name="type"
          rules={{
            required: INLINE_ERROR_MSGS.TYPE,
          }}
          render={({ field: { onChange, value } }) => (
            <div className={c.field}>
              <label>Type</label>
              <span>
                <Dropdown
                  value={value}
                  data={CATEGORY_TYPES}
                  onChange={onChange}
                  onClose={() => trigger('type')}
                />
                {errorRow([errors.type])}
              </span>
            </div>
          )}
        />

        <Subcategories
          control={control}
          getValues={getValues}
          errors={errors}
          links={links}
          errorRow={errorRow}
          refreshData={refreshData}
          setHistory={setHistory}
        />
      </div>
    </SlideOutForm>
  );
};
export default CategoryForm;
